<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="440"
    @input="$emit('input', $event)">
    <div class="d-flex align-center px-4 py-3">
      <v-icon class="mr-2">
        mdi-history
      </v-icon>
      <div>
        <div class="subtitle-1 font-weight-bold">
          {{ title || 'ประวัติการแก้ไข' }}
        </div>
        <div
          v-if="total"
          class="caption grey--text">
          <template v-if="totalGroups">
            ทั้งหมด {{ totalGroups }} ครั้ง ·
          </template>
          {{ total }} รายการ
        </div>
      </div>
      <v-spacer />
      <v-btn
        icon
        @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider />

    <div
      v-if="loading && !logs.length"
      class="pa-6 text-center">
      <v-progress-circular
        indeterminate
        size="24" />
    </div>
    <div
      v-else-if="!logs.length"
      class="pa-6 text-center grey--text">
      ยังไม่มีประวัติการแก้ไข
    </div>
    <div
      v-else
      class="px-4 pb-4">
      <div
        v-for="entry in entries"
        :key="entry.key"
        class="history-entry">
        <v-avatar
          size="32"
          :color="isSystem(entry.first) ? 'grey lighten-3' : 'primary lighten-4'"
          class="mr-3 flex-shrink-0">
          <span class="avatar-text">{{ initials(entry.first) }}</span>
        </v-avatar>
        <div class="history-body">
          <div class="d-flex align-center">
            <span class="actor text-truncate">{{ actor(entry.first) }}</span>
            <v-spacer />
            <span class="time">{{ formatDateTime(entry.first.createdAt) }}</span>
          </div>
          <div class="d-flex align-center mt-1">
            <v-chip
              x-small
              label
              :color="entry.timeline ? activityStyles.date.color : activityStyle(entry.first).color"
              :text-color="entry.timeline ? activityStyles.date.textColor : activityStyle(entry.first).textColor">
              {{ entry.timeline ? timelineLabel(entry.timeline) : activityLabel(entry.first) }}
            </v-chip>
            <span
              v-if="entry.items.length > 1"
              class="time ml-2">
              {{ entry.items.length }} task
            </span>
            <span
              v-if="showStage && !entry.timeline && !entry.completion && !hasChanges(entry)"
              class="stage-inline text-truncate ml-2">
              {{ stageNames(entry) }}
            </span>
          </div>

          <div
            v-if="entry.timeline"
            class="change-box change-row">
            <span class="change-label change-label--wide">Timeline</span>
            <template v-if="entry.timeline.old">
              <span class="old-date">{{ entry.timeline.old.name || '-' }}</span>
              <v-icon
                x-small
                class="mx-1">
                mdi-arrow-right
              </v-icon>
            </template>
            <span class="new-date">{{ entry.timeline.next ? entry.timeline.next.name || '-' : '-' }}</span>
          </div>
          <div
            v-else-if="hasChanges(entry)"
            class="change-box">
            <div
              v-for="item in entry.items"
              :key="item.id"
              class="change-item">
              <div
                v-if="showStage"
                class="stage-name">
                {{ stageName(item) }}
              </div>
              <div
                v-for="change in changes(item)"
                :key="change.field"
                class="change-row">
                <span class="change-label">{{ change.label }}</span>
                <span class="old-date">{{ formatDate(change.old) }}</span>
                <v-icon
                  x-small
                  class="mx-1">
                  mdi-arrow-right
                </v-icon>
                <span class="new-date">{{ formatDate(change.next) }}</span>
                <span
                  v-if="change.shift"
                  class="shift"
                  :class="change.shift > 0 ? 'shift--later' : 'shift--earlier'">
                  {{ change.shift > 0 ? `+${change.shift}` : change.shift }} วัน
                </span>
              </div>
            </div>
          </div>
          <div
            v-else-if="entry.completion"
            class="change-box">
            <div
              v-if="showStage"
              class="stage-name">
              {{ stageName(entry.first) }}
            </div>
            <div
              v-if="entry.completion.cancelled"
              class="change-row">
              <span class="change-label">Start</span>
              <span>{{ formatDate(entry.completion.start) }}</span>
            </div>
            <div class="change-row">
              <span class="change-label">Due</span>
              <span>{{ formatDate(entry.completion.due) }}</span>
            </div>
            <div
              v-if="!entry.completion.cancelled"
              class="change-row">
              <span class="change-label">Done</span>
              <span class="new-date">{{ formatDate(entry.completion.done) }}</span>
            </div>
          </div>

          <div
            v-if="entry.first.note"
            class="note">
            <v-icon
              x-small
              class="mr-1">
              mdi-comment-text-outline
            </v-icon>
            {{ entry.first.note }}
          </div>
        </div>
      </div>

      <div
        v-if="page < pages && logs.length < total"
        class="text-center mt-2">
        <v-btn
          text
          small
          :loading="loading"
          @click="fetchLogs(page + 1)">
          โหลดเพิ่ม
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapActions } from 'vuex'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'

const productionPlanService = new ProductionPlanProvider()

const ACTIVITY_LABELS = {
  create_task: 'สร้าง task',
  delete_task: 'ลบ task',
  change_due_date: 'แก้ Due date',
  change_start_date: 'แก้ Start date',
  change_sales_date: 'แก้ Sales date',
  change_timeline: 'เปลี่ยน Timeline',
  assign_timeline: 'กำหนด Timeline',
  complete_task: 'Complete',
  un_complete_task: 'ยกเลิก Complete'
}

const BULK_SOURCE_LABELS = {
  bulk_change_date: 'แก้ Stage',
  batch_change_date: 'แก้จาก Gantt',
  copy_timeline: 'Copy Timeline',
  import_task_date: 'Import'
}

const ACTIVITY_STYLES = {
  date: { color: 'blue lighten-5', textColor: 'blue darken-2' },
  done: { color: 'green lighten-5', textColor: 'green darken-2' },
  removed: { color: 'red lighten-5', textColor: 'red darken-2' },
  other: { color: 'grey lighten-3', textColor: 'grey darken-2' }
}

const ACTIVITY_GROUPS = {
  change_due_date: 'date',
  change_start_date: 'date',
  bulk_change_date: 'date',
  change_sales_date: 'date',
  change_timeline: 'date',
  complete_task: 'done',
  delete_task: 'removed'
}

const FIELD_LABELS = {
  startDate: 'Start',
  dueDate: 'Due'
}

export default {
  name: 'TaskHistoryDrawer',

  props: {
    value: {
      type: Boolean,
      required: true
    },
    taskId: {
      type: String,
      default: null
    },
    prototypeId: {
      type: String,
      default: null
    },
    stages: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      activityStyles: ACTIVITY_STYLES,
      logs: [],
      loading: false,
      page: 1,
      pages: 1,
      total: 0,
      totalGroups: 0
    }
  },

  computed: {
    showStage () {
      return !this.taskId
    },

    entries () {
      const grouped = this.logs.reduce((acc, log) => {
        const last = acc[acc.length - 1]
        if (last && log.groupId && last.first.groupId === log.groupId) {
          last.items.push(log)
        } else {
          acc.push({ key: log.id, first: log, items: [log] })
        }
        return acc
      }, [])

      return grouped.map((entry) => ({
        ...entry,
        items: [...entry.items].sort((a, b) => this.stageOrder(a) - this.stageOrder(b)),
        timeline: this.timelineChange(entry),
        completion: this.completion(entry)
      }))
    },

    stageNameMap () {
      return Object.fromEntries(this.stages.map((stage) => [stage.id, stage.name]))
    },

    stageOrderMap () {
      return Object.fromEntries(this.stages.map((stage, index) => [stage.id, index]))
    }
  },

  watch: {
    value (val) {
      if (val) this.fetchLogs(1)
    },
    taskId () {
      if (this.value) this.fetchLogs(1)
    },
    prototypeId () {
      if (this.value) this.fetchLogs(1)
    }
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),

    async fetchLogs (page) {
      if (!this.taskId && !this.prototypeId) return

      const filter = this.taskId ? { taskId: this.taskId } : { prototypeId: this.prototypeId }
      if (page === 1) this.fetchSummary(filter)

      this.loading = true
      try {
        const { data } = await productionPlanService.getTaskLogs({ ...filter, page, limit: 20 })
        const results = data?.results ?? []
        const logs = page === 1 ? results : [...this.logs, ...results]
        this.page = data?.page ?? page
        this.pages = data?.pages ?? 1
        this.total = data?.total ?? logs.length

        const lastGroupId = logs[logs.length - 1]?.groupId
        if (lastGroupId && this.page < this.pages) {
          const { data: groupData } = await productionPlanService.getTaskLogs({ ...filter, groupId: lastGroupId, page: 1, limit: 100 })
          logs.push(...(groupData?.results ?? []))
        }

        const seenIds = new Set()
        this.logs = logs.filter((log) => {
          if (seenIds.has(log.id)) return false
          seenIds.add(log.id)
          return true
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'ดึงประวัติไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async fetchSummary (filter) {
      try {
        const { data } = await productionPlanService.getTaskLogSummary(filter)
        this.totalGroups = data?.totalGroups ?? 0
      } catch (error) {
        this.totalGroups = 0
      }
    },

    actor (log) {
      return log.createdBy?.email ?? 'system'
    },

    isSystem (log) {
      return this.actor(log) === 'system'
    },

    initials (log) {
      return this.actor(log).slice(0, 2).toUpperCase()
    },

    activityLabel (log) {
      if (log.activity === 'bulk_change_date') {
        return BULK_SOURCE_LABELS[log.remark] ?? 'แก้หลาย task'
      }
      return ACTIVITY_LABELS[log.activity] ?? log.activity
    },

    activityStyle (log) {
      return ACTIVITY_STYLES[ACTIVITY_GROUPS[log.activity] ?? 'other']
    },

    stageNames (entry) {
      return [...new Set(entry.items.map((item) => this.stageName(item)))].join(', ')
    },

    completion (entry) {
      const log = entry.first
      const cancelled = log.activity === 'un_complete_task'
      if (entry.items.length > 1 || !(cancelled || log.activity === 'complete_task')) return null

      return { start: log.taskId?.startDate, due: log.taskId?.dueDate, done: log.createdAt, cancelled }
    },

    hasChanges (entry) {
      return entry.items.some((item) => this.changes(item).length)
    },

    dayShift (oldDate, nextDate) {
      if (!oldDate || !nextDate) return 0
      return this.$dayjs(nextDate).startOf('day').diff(this.$dayjs(oldDate).startOf('day'), 'day')
    },

    timelineChange (entry) {
      const timeline = entry.items.find((item) => item.changeInfo?.timeline)?.changeInfo.timeline
      if (!timeline?.next || String(timeline.old?.id) === String(timeline.next.id)) return null
      return timeline
    },

    timelineLabel (timeline) {
      return timeline.old ? ACTIVITY_LABELS.change_timeline : ACTIVITY_LABELS.assign_timeline
    },

    stageOrder (log) {
      return this.stageOrderMap[log.taskId?.stageId] ?? Number.MAX_SAFE_INTEGER
    },

    stageName (log) {
      const stageId = log.taskId?.stageId
      return this.stageNameMap[stageId] ?? log.taskId?.sketchId ?? '-'
    },

    changes (log) {
      const old = log.changeInfo?.old
      const next = log.changeInfo?.next

      if (next) {
        return Object.keys(FIELD_LABELS)
          .filter((field) => next[field])
          .map((field) => ({
            field,
            label: FIELD_LABELS[field],
            old: old?.[field],
            next: next[field],
            shift: this.dayShift(old?.[field], next[field])
          }))
      }

      const [, activity, oldDate, nextDate] = (log.remark ?? '').split(':')
      const field = { change_due_date: 'dueDate', change_start_date: 'startDate' }[activity]
      if (field && oldDate && nextDate) {
        return [{
          field,
          label: FIELD_LABELS[field],
          old: oldDate,
          next: nextDate,
          shift: this.dayShift(oldDate, nextDate)
        }]
      }

      return []
    },

    formatDate (date) {
      return date ? this.$dayjs(date).format('DD/MM/YYYY') : '-'
    },

    formatDateTime (date) {
      return date ? this.$dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    }
  }
}
</script>

<style scoped>
.history-entry {
  display: flex;
  padding: 14px 0;
  border-bottom: 1px solid #eeeeee;
}
.history-body {
  flex: 1;
  min-width: 0;
  font-size: 13px;
}
.avatar-text {
  font-size: 11px;
  font-weight: 600;
}
.actor {
  font-weight: 600;
  color: #1c2433;
}
.time {
  font-size: 12px;
  color: #8a94a6;
  white-space: nowrap;
}
.change-box {
  margin-top: 10px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 8px;
}
.change-item + .change-item {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e6e9ef;
}
.stage-name {
  font-weight: 600;
  margin-bottom: 2px;
}
.stage-inline {
  font-size: 12px;
  font-weight: 600;
  color: #1c2433;
}
.change-row {
  display: flex;
  align-items: center;
  line-height: 24px;
}
.change-label {
  width: 40px;
  flex-shrink: 0;
  font-size: 12px;
  color: #8a94a6;
}
.change-label--wide {
  width: 64px;
}
.old-date {
  text-decoration: line-through;
  color: #9e9e9e;
}
.new-date {
  font-weight: 600;
  color: #1c2433;
}
.shift {
  margin-left: 8px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 18px;
  border-radius: 4px;
  white-space: nowrap;
}
.shift--later {
  background: #fff3e0;
  color: #e65100;
}
.shift--earlier {
  background: #e8f5e9;
  color: #2e7d32;
}
.note {
  margin-top: 8px;
  color: #6b7686;
}
</style>
